import * as v from 'valibot';
import type { AuthStatus } from '@framework/auth/authStatus';
import type { UserId } from 'balus-common';
import { ServerValue } from '@framework/repository';
import { RTDBPath, UserSurveyAnswerSchema } from 'balus-common';
import { Timestamp } from '@framework/Timestamp';
import { EntityRepository } from '@framework/repository/EntityRepository/EntityRepository';
import { Random } from '@framework/Random';

type UserSurveyDisplayStatus = { status: 'Display'; id: string } | { status: 'NoDisplay' };
type UserSurveyAnswerStatus = 'AlreadyAnswered' | 'ReadyToAnswer' | 'Canceled' | 'Error';

const UserSurveyAnswerRepository = EntityRepository.create({
  name: 'UserSurveyAnswerRepository',
  schema: UserSurveyAnswerSchema,
  toJson: (entity: v.InferOutput<typeof UserSurveyAnswerSchema>) => ({
    ...entity,
    answeredAt: entity.answeredAt?.getTime() ?? null,
    canceledAt: entity.canceledAt?.getTime() ?? null,
  }),
  buildPath: RTDBPath.User.userSurveyAnswerPath,
});

type UserSurveyAnswer = v.InferOutput<typeof UserSurveyAnswerSchema>;

const buildSurveyAnswer = (current: UserSurveyAnswer | null) =>
  v.parse(UserSurveyAnswerSchema, {
    surveyId: current?.surveyId || Random.generateRandomID(),
    answeredAt: current?.answeredAt?.getTime() ?? null,
    canceledAt: current?.canceledAt?.getTime() ?? null,
  });

export class UserSurveyOperation {
  static async answer(userId: UserId): Promise<UserSurveyAnswerStatus> {
    const status = await UserSurveyAnswerRepository.get(userId);
    if (status?.answeredAt) {
      return 'AlreadyAnswered';
    }

    try {
      await UserSurveyAnswerRepository.set(userId, buildSurveyAnswer(status));
      await UserSurveyAnswerRepository.ref(userId).reference.child('answeredAt').set(ServerValue.TIMESTAMP);

      return 'ReadyToAnswer';
    } catch (e) {
      console.error(e);
      return 'Error';
    }
  }

  static async cancel(userId: UserId) {
    const status = await UserSurveyAnswerRepository.get(userId);
    if (status?.answeredAt) {
      return 'AlreadyAnswered';
    }

    try {
      await UserSurveyAnswerRepository.set(userId, buildSurveyAnswer(status));
      await UserSurveyAnswerRepository.ref(userId).reference.child('canceledAt').set(ServerValue.TIMESTAMP);

      return 'Canceled';
    } catch (e) {
      console.error(e);
      return 'Error';
    }
  }

  static async getDisplayStatus(auth: AuthStatus): Promise<UserSurveyDisplayStatus> {
    if (auth.signInAt === null || auth.currentUserId === null || auth.userType === 'anonymous') {
      return { status: 'NoDisplay' };
    }

    const status = await UserSurveyAnswerRepository.get(auth.currentUserId);
    if (!status) {
      // 本機能導入前から存在するユーザの場合、レコードがない
      return { status: 'NoDisplay' };
    }

    if (status.answeredAt) {
      // 回答済み
      return { status: 'NoDisplay' };
    }

    if (!status.canceledAt) {
      // 新規作成ユーザ（回答もキャンセルもしていない）の場合、アンケートを表示する
      return { status: 'Display', id: status.surveyId };
    }

    if (status.canceledAt && status.canceledAt.getTime() < auth.signInAt) {
      const canceledAt = new Timestamp(status.canceledAt.getTime());
      if (canceledAt.isBefore(Timestamp.now().addDays(-14))) {
        // 回答キャンセルがログイン前、かつ２週間より前の場合はアンケートを再表示する
        return { status: 'Display', id: status.surveyId };
      }
    }

    return { status: 'NoDisplay' };
  }
}
